import React from 'react';
import { useLiveMatch } from '../hooks/useLiveMatch';
import { EventCreationForm } from './EventCreationForm';
import { LineupQuickActions } from './LineupQuickActions';
import { ConfirmModal } from './ConfirmModal';
import { SubstitutionModal } from './SubstitutionModal';
import { CaptainTransferModal } from './CaptainTransferModal';
import { ScoreDisplay } from './ScoreDisplay';
import { MatchTimeControls } from './MatchTimeControls';
import { MatchStatusControl } from './MatchStatusControl';
import { VARReviewModal } from './VARReviewModal';
import { OperatorModeSelector } from './OperatorModeSelector';

export const LiveMatchViewDefault = ({ matchId }) => {
  const m = useLiveMatch(matchId);

  if (!m.connected) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-12">
          <div className="text-center">
            <div className="text-6xl mb-4">🔄</div>
            <h2 className="text-2xl font-bold mb-2">Connecting to Match...</h2>
            <p className="text-gray-500">Establishing WebSocket connection</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="p-6 max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <div className="flex justify-between items-start mb-2">
            <h1 className="text-3xl font-bold">Live Match #{matchId}</h1>
            <div className="flex gap-2 items-center">
              <OperatorModeSelector currentMode="default" matchId={matchId} />
              <button
                onClick={m.openPublicView}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                📺 Open Public View
              </button>
              <button
                onClick={m.openOBSView}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                🎥 Vision Master
              </button>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <span className={`px-3 py-1 rounded-full ${m.connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {m.connected ? '🟢 Connected' : '🔴 Disconnected'}
            </span>
            <span className={`px-3 py-1 rounded-full ${m.joined ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
              {m.joined ? '📺 Joined' : '⏳ Not joined'}
            </span>
            <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-800">
              👥 {m.viewers} viewers
            </span>
          </div>
        </div>

        {/* Captain Alert */}
        {m.captainAlert && (
          <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">⚠️</span>
                <div>
                  <h3 className="font-bold text-red-800 text-lg">Captain Required!</h3>
                  <p className="text-red-700">
                    {m.captainAlert.playerName} was expelled and lost captaincy.
                    Please assign a new captain for the team.
                  </p>
                </div>
              </div>
              <button
                onClick={() => m.setCaptainAlert(null)}
                className="text-red-600 hover:text-red-800 font-bold text-xl"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Match Status Control Component */}
        <MatchStatusControl
          match={m.match}
          lineupStatus={m.lineupStatus}
          onStartMatch={() => m.setConfirmModal({
            isOpen: true,
            action: 'start',
            title: 'Start Match',
            message: 'Start this match? The operator will control when to start the clock.'
          })}
          onEndMatch={() => {
            if (m.clock.running) {
              alert('Please stop the clock before ending the match');
              return;
            }
            m.setConfirmModal({
              isOpen: true,
              action: 'end',
              title: 'End Match',
              message: 'End this match? This action cannot be undone.'
            });
          }}
          onCancelMatch={() => m.setConfirmModal({
            isOpen: true,
            action: 'cancel',
            title: 'Cancel Match',
            message: 'Cancel this match? The clock will be stopped if running.'
          })}
          onNavigateToLineup={() => m.navigate(`/lineups/${matchId}`)}
          clockRunning={m.clock.running}
          captainsAssigned={m.captainsAssigned()}
        />

        {/* Score Display Component */}
        <ScoreDisplay
          score={m.score}
          homeTeam={m.match?.home_team}
          awayTeam={m.match?.away_team}
          onUpdateScore={m.handleScoreUpdate}
          scoreboardVisible={m.scoreboardVisible}
          onToggleScoreboard={m.toggleScoreboard}
          scoreboardTimeout={m.scoreboardTimeout}
          onScoreboardTimeoutChange={m.handleScoreboardTimeoutChange}
          formationsVisible={m.formationsVisible}
          onToggleFormations={m.toggleFormations}
          logoVisible={m.logoVisible}
          onToggleLogo={m.toggleLogo}
          match={m.match}
          joined={m.joined}
        />

        {/* Match Time Controls Component */}
        <MatchTimeControls
          clock={m.clock}
          onStartClock={m.handleStartClock}
          onStopClock={m.stopClock}
          onResetClock={m.resetClock}
          onSetTime={m.setTime}
          onSetInjuryTime={m.setInjuryTime}
          onChangeHalf={m.changeHalf}
          joined={m.joined}
        />

        {/* Quick Actions - Lineup with buttons */}
        {!m.lineupLoading && (
          <div className="mb-6">
            <LineupQuickActions
              matchId={matchId}
              homeTeam={m.homeTeam}
              awayTeam={m.awayTeam}
              activeCounts={m.activeCounts}
              onEventCreated={m.handleQuickEvent}
            />
          </div>
        )}

        {/* Recent Events with VAR */}
        {m.events.length > 0 && (
          <div className="mb-6">
            <details open className="bg-gray-50 rounded-lg">
              <summary className="cursor-pointer p-4 font-semibold text-gray-700 hover:bg-gray-100 rounded-lg">
                Recent Events ({m.events.length})
              </summary>
              <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                {[...m.events].reverse().slice(0, 20).map((evt) => {
                  const evtPlayerName = evt.player
                    ? `${evt.player.first_name} ${evt.player.last_name}`
                    : 'Unknown';
                  const isVAReligible = ['goal', 'penalty_goal', 'own_goal', 'penalty', 'red_card'].some(t =>
                    evt.event_type?.toLowerCase().includes(t)
                  );
                  return (
                    <div
                      key={evt.id}
                      className={`flex items-center justify-between p-2 rounded-lg border ${
                        evt.is_cancelled ? 'bg-red-50 border-red-200 opacity-60' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 text-sm">
                        <span className="font-mono font-bold text-gray-600 w-8">
                          {evt.minute}'
                        </span>
                        <span className="font-semibold">
                          {evtPlayerName}
                        </span>
                        <span className="text-gray-500">
                          {evt.event_type?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-400">
                          {evt.team?.short_name || evt.team?.name || ''}
                        </span>
                        {evt.is_cancelled && (
                          <span className="text-xs text-red-600 font-semibold">CANCELLED</span>
                        )}
                      </div>
                      {!evt.is_cancelled && (
                        <div className="flex gap-2">
                          {isVAReligible && (
                            <button
                              onClick={() => m.setVarModal({ isOpen: true, event: evt })}
                              className="px-3 py-1 bg-blue-700 text-white text-xs font-bold rounded hover:bg-blue-800 transition-colors"
                            >
                              VAR
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          </div>
        )}

        {/* Event Creation - Fallback form */}
        <div className="mb-6">
          <details className="bg-gray-50 rounded-lg">
            <summary className="cursor-pointer p-4 font-semibold text-gray-700 hover:bg-gray-100 rounded-lg">
              Advanced Event Creation
            </summary>
            <div className="p-4">
              <EventCreationForm
                matchId={matchId}
                clock={m.clock}
                onEventCreated={m.createEvent}
              />
            </div>
          </details>
        </div>

      </div>
    </div>

    {/* Confirmation Modal */}
    <ConfirmModal
      isOpen={m.confirmModal.isOpen}
      title={m.confirmModal.title}
      message={m.confirmModal.message}
      confirmText={m.confirmModal.action === 'start' ? 'Start Match' : m.confirmModal.action === 'end' ? 'End Match' : 'Cancel Match'}
      confirmClass={
        m.confirmModal.action === 'start' ? 'bg-green-600 hover:bg-green-700' :
        m.confirmModal.action === 'end' ? 'bg-blue-600 hover:bg-blue-700' :
        'bg-red-600 hover:bg-red-700'
      }
      onConfirm={() => {
        if (m.confirmModal.action === 'start') m.handleStartMatch();
        else if (m.confirmModal.action === 'end') m.handleEndMatch();
        else if (m.confirmModal.action === 'cancel') m.handleCancelMatch();
      }}
      onCancel={() => m.setConfirmModal({ isOpen: false, action: null, title: '', message: '' })}
    />

    {/* Substitution Modal */}
    <SubstitutionModal
      isOpen={m.substitutionModal.isOpen}
      outgoingPlayer={m.substitutionModal.outgoingPlayer}
      benchPlayers={m.benchPlayersForSubstitution}
      onConfirm={m.handleSubstitutionConfirm}
      onCancel={() => m.setSubstitutionModal({ isOpen: false, outgoingPlayer: null })}
    />

    {/* Captain Transfer Modal */}
    <CaptainTransferModal
      isOpen={m.captainTransferModal.isOpen}
      currentCaptain={m.captainTransferModal.currentCaptain}
      eligiblePlayers={m.eligiblePlayersForCaptain}
      onTransfer={m.handleCaptainTransfer}
      onCancel={() => m.setCaptainTransferModal({ isOpen: false, currentCaptain: null, pendingSubstitution: null })}
    />

    {/* VAR Review Modal */}
    <VARReviewModal
      isOpen={m.varModal.isOpen}
      event={m.varModal.event}
      matchId={matchId}
      onClose={() => m.setVarModal({ isOpen: false, event: null })}
    />
    </>
  );
};
